import React, { memo, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/utility/chevron-up';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import commonMessages from './messages';

type Props = {
	title: string;
	messages: MessageDescriptor[];
	automationSystemRules?: MessageDescriptor[];
};

export const DetailsList = memo<Props>(({ title, messages, automationSystemRules }: Props) => {
	const { formatMessage } = useIntl();
	const [expanded, setExpanded] = useState<boolean>(false);

	const handleOnClick = () => {
		setExpanded((prevState) => !prevState);
	};

	return (
		<Box xcss={wrapperStyles}>
			<Button
				appearance="subtle-link"
				spacing="none"
				iconBefore={
					expanded ? (
						<Box xcss={iconWrapperStyles}>
							<ChevronUpIcon
								label={formatMessage(commonMessages.expandedIconLabel)}
								color={token('color.icon', colors.N800)}
							/>
						</Box>
					) : (
						<Box xcss={iconWrapperStyles}>
							<ChevronRightIcon
								label={formatMessage(commonMessages.collapsedIconLabel)}
								color={token('color.icon', colors.N800)}
							/>
						</Box>
					)
				}
				onClick={handleOnClick}
			>
				<Title>{title}</Title>
			</Button>
			{expanded && (
				<List data-testid="servicedesk-itsm-features.common.ui.details-list.list">
					{automationSystemRules && (
						<>
							<li key={commonMessages.automationDetails.id}>
								{formatMessage(commonMessages.automationDetails)}
							</li>
							<ul>
								{automationSystemRules.map((automation) => (
									<li key={automation.id}>{formatMessage(automation)}</li>
								))}
							</ul>
						</>
					)}
					{messages.map((message) => (
						<li key={message.id}>{formatMessage(message)}</li>
					))}
				</List>
			)}
		</Box>
	);
});

const wrapperStyles = xcss({
	marginTop: 'space.200',
	marginLeft: 'space.negative.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N800),
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	marginLeft: token('space.200', '16px'),
});

const iconWrapperStyles = xcss({
	width: '24px',
});
